import { cn } from '@/design-system/utils/cn';
import { X } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from './Button';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export function Dialog({ isOpen, onClose, title, description, icon, children, footer, closeOnOverlayClick = false, closeOnEscape = false, showCloseButton = true, size = 'md' }: DialogProps) {
  const descriptionId = useId();
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content onEscapeKeyDown={(event) => { if (!closeOnEscape) event.preventDefault(); }} onPointerDownOutside={(event) => { if (!closeOnOverlayClick) event.preventDefault(); }} aria-describedby={description ? descriptionId : undefined} className={cn('fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100%_-_2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl outline-none', sizes[size])}>
        {/* Header */}
        {title && (
          <div data-dialog-header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-100 p-4">
            <div className="flex min-w-0 items-center gap-3">
              {icon && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-200 text-slate-700 [&_svg]:h-5 [&_svg]:w-5" aria-hidden="true">{icon}</span>}
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-lg font-semibold text-slate-900">{title}</DialogPrimitive.Title>
                {description && <DialogPrimitive.Description id={descriptionId} className="mt-1 text-sm text-slate-500">{description}</DialogPrimitive.Description>}
              </div>
            </div>
            {showCloseButton && <DialogPrimitive.Close asChild><Button type="button" variant="ghost" size="sm" aria-label="Fechar"><X className="text-gray-500" /></Button></DialogPrimitive.Close>}
          </div>
        )}

        {!title && <DialogPrimitive.Title className="sr-only">Diálogo</DialogPrimitive.Title>}
        <div data-dialog-content className="min-h-0 flex-1 overflow-y-auto bg-white p-4">
          {children}
        </div>
        {footer && <div data-dialog-footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 p-4">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'default';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'default',
  isLoading = false,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={title} size="sm" footer={<>
          <Button variant="secondary" type="button"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button variant={variant === 'danger' ? 'danger' : 'primary'} type="button"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processando...' : confirmText}
          </Button>
    </>}>
      <p className="text-gray-600">{message}</p>
    </Dialog>
  );
}

interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  /** Botão extra (ex.: "Ir para pagamento") */
  actionLabel?: string;
  onAction?: () => void;
}

export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  actionLabel,
  onAction,
}: AlertDialogProps) {
  const variants = {
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={title} size="sm" footer={<>
          {actionLabel && onAction && (
            <Button
              type="button"
              onClick={() => {
                onAction();
                onClose();
              }}
            >
              {actionLabel}
            </Button>
          )}
          <Button
            type="button"
            onClick={onClose}
          >
            OK
          </Button>
    </>}>
      <p className={cn('text-sm', variants[variant])}>{message}</p>
    </Dialog>
  );
}
